<script setup>
import {computed, ref, onMounted, onBeforeUnmount, watch, nextTick} from 'vue'
import {useField} from "vee-validate";
import useDebouncedRef from '~/composables/useDebouncedRef'

const props = defineProps({
  name: {
    type: String,
    default: ''
  },
  id: {
    type: String,
    default: ''
  },
  autocomplete: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  icon: {
    type: String,
    default: ''
  },
  options: {
    type: Array,
    default: null
  },
  type: {
    type: String,
    default: 'text'
  },
  components: {
    type: Object,
    default: null
  },
  modelValue: {
    type: [String, Number, Boolean, Array, Object],
    default: ''
  },
  required: Boolean,
  borderless: Boolean,
  transparent: Boolean,
  borderbottom: Boolean,
  borderradiusnone: Boolean,
  taggable: Boolean,
  reduce: {
    type: String,
    default: 'name'
  },
  disabled: Boolean,
})

const emit = defineEmits(['update:modelValue', 'change'])

const computedValue = computed({
  get: () => props.modelValue,
  set: value => {
    emit('update:modelValue', value)
  },

})

const computedType = computed(() =>
    props.options ? 'select' : props.type,
)

const inputEl = ref(null)

const observer = ref(null);

const limit = ref(20);

const search = useDebouncedRef('', 500);

const onSearch = (query) => {
  search.value = query;

}

const filtered = computed(() => {

  if (search.value.length > 0 && !props.taggable) {
    let options = [];
    let final_options = [];
    options = props.options.filter((option) => option.name.toLowerCase() === search.value.toLowerCase());
    final_options = final_options.concat(options);

    options = props.options.filter((option) => option.name.split(' ')[0].toLowerCase() === search.value.trim().toLowerCase()
        && option.name.toLowerCase() !== search.value.toLowerCase());

    final_options = final_options.concat(options);

    options = props.options.filter((option) => option.name.toLowerCase().includes(search.value.toLowerCase())
        && option.name.split(' ')[0].toLowerCase() !== search.value.trim().toLowerCase()
        && option.name.toLowerCase() !== search.value.toLowerCase()
    );

    final_options = final_options.concat(options);

    return final_options;
  } else {

    let options = [];
    options = props.options.filter((option) => option.name.toLowerCase().includes(search.value.toLowerCase()));

    if(options.length <= 0 && search.value.length > 0 && props.reduce === 'horse_name')
    {
      options.push({
        horse_name: search.value,
        name: search.value
      })
    }

    return options;
  }
});

const paginated = computed(() => {

  if (props.modelValue > 0 && props.options.length > limit.value) {
    let item = props.options.filter((option) => option.id === computedValue.value);
    filtered.value.splice(filtered.value.map((item) => item.id).indexOf(item[0].id), 1);
    filtered.value.unshift({id: item[0].id, name: item[0].name})
  }

  return filtered.value.slice(0, limit.value);
});

const name = props.name;
const {
  Value: value,
  errorMessage,
  meta,
} = useField(name, undefined, {
  initialValue: props.modelValue,
});


const selectElClass = computed(() => {
  const base = [
    props.borderless ? 'border-0' : 'border',
    props.borderbottom ? 'border-b border-stone-900' : 'border',
    props.borderradiusnone ? 'rounded-none' : '',
  ]
  return base
})

const hasNextPage =computed(()=>{
  return paginated.value.length < filtered.value.length
})

const infiniteScroll = async ([{ isIntersecting, target}]) => {
  if (isIntersecting) {
    const ul = target.offsetParent
    const scrollTop = target.offsetParent.scrollTop
    limit.value += 10
    await nextTick();
    ul.scrollTop = scrollTop
  }
}

const load = ref(null);

const onOpen = async () => {
  if(hasNextPage.value){
    await nextTick();
    observer.value.observe(load.value)
  }
}

const onClose = () => {
  observer.value.disconnect();
}

watch(computedValue, newVal => {
  emit('change');
})
onMounted(() => {
  observer.value = new IntersectionObserver(infiniteScroll);
  if( props.name === 'breed_result_id'){
    inputEl.value.searchEl.focus();
    setTimeout(()=>{
      inputEl.value.searchEl.focus();
    },1000)
  }
})
</script>
<template>
  <div class="relative">
    <V-Field v-if="reduce==='name'" v-model="computedValue" :name="name" :id="id">
      <vSelect v-model="computedValue" :options="options" :reduce="(option) => option.name"
               label="name" :taggable="taggable" :components="components"
               :disabled="disabled"
               class="v-select-main"
               :class="selectElClass"
               selectOnTab
      />
    </V-Field>
    <V-Field v-else v-if="reduce==='id'" v-model="computedValue" :name="name" :id="id">
      <vSelect v-model="computedValue" :reduce="(option) => option.id" :components="components"
               label="name"
               :options="paginated"
               :filterable="false"
               @search="onSearch"
               :disabled="disabled"
               ref="inputEl"
               :taggable="taggable"
               class="v-select-main"
               :clearable="false"
               :class="selectElClass"
               @open="onOpen"
               @close="onClose"
               selectOnTab
      >
        <template #list-footer>
          <li v-show="hasNextPage" ref="load" class="loader">
            Loading more options...
          </li>
        </template>
      </vSelect>
    </V-Field>
    <V-Field v-if="reduce==='horse_name'" v-model="computedValue" :name="name" :id="id">
      <vSelect v-model="computedValue" :reduce="(option) => option.name"
               label="name" :components="components"
               :disabled="disabled"
               class="v-select-main"
               :options="paginated"
               @search="onSearch"
               ref="inputEl"
               :filterable="false"
               :clearable="false"
               :class="selectElClass"
               @open="onOpen"
               @close="onClose"
               selectOnTab
      >
        <template #list-footer>
          <li v-show="hasNextPage" ref="load" class="loader">
            Loading more options...
          </li>
        </template>
      </vSelect>
    </V-Field>
    <p class="text-red-600" v-show="(errorMessage || meta.valid)">
      {{ errorMessage }}
    </p>
  </div>
</template>
<style scoped>
:deep(.v-select-main) {
  --vs-dropdown-z-index: 30;
}
.loader {
  text-align: center;
  color: #bbbbbb;
}
</style>
